"use client";

// UIArc Card — primitives de SURFACE partagées (source MIT arc-library@7238f981 registry/components/card : la carte
// pointée se soulève de 2 px sur `spring.snappy`, l'ombre et la bordure suivent `data-hover`, rien ne bouge en mouvement
// réduit ni au toucher). Un seul code pour :
//   · la composition riche UIArc `Card` (titre, média, aperçu qui grandit en Dialog) — ./card.tsx ;
//   · la façade historique `@ubm/ui` `Card` (surface href/asChild/variant/padding/radius) — ../../primitives/Card.tsx.
// Peintre commun : la classe `.card` du module UIArc (bordure, rayon, surface, ombre et bordure au survol via data-hover),
// complétée pour la surface par variant/padding/radius/interactif — aucun Tailwind, aucune seconde peinture.
import { forwardRef, useState, type AnchorHTMLAttributes, type HTMLAttributes, type Ref } from "react";
import { Slot } from "@radix-ui/react-slot";
import { motion, useReducedMotionConfig as useReducedMotion, type HTMLMotionProps, type MotionProps } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./card.module.css";

/** La levée UIArc d'une carte pointée. */
export const CARD_LIFT = { y: -2 } as const;
export const cardLiftTransition = motionTokens.spring.snappy;

/**
 * État de survol + props Motion de la levée. `enabled=false` (surface non interactive) ou mouvement réduit → aucune levée,
 * `data-hover` reste exposé pour la peinture.
 */
export function useCardLift(enabled = true): { hovered: boolean; motionProps: MotionProps & { "data-hover"?: true } } {
  const reduced = useReducedMotion() ?? false;
  const [hovered, setHovered] = useState(false);
  return {
    hovered,
    motionProps: {
      whileHover: enabled && !reduced ? CARD_LIFT : undefined,
      onHoverStart: () => setHovered(true),
      onHoverEnd: () => setHovered(false),
      "data-hover": hovered || undefined,
    },
  };
}

const MotionSlot = motion.create(Slot);

export type CardSurfaceVariant = "raised" | "sunken" | "outline";
export type CardSurfacePadding = "none" | "xs" | "sm" | "md" | "lg";
export type CardSurfaceRadius = "none" | "sm" | "md" | "lg" | "full";

/** Classes canon d'une surface de carte (même peintre que la Card UIArc). */
export function cardSurfaceClassName({ variant = "raised", padding = "md", radius = "lg", interactive = false }: { variant?: CardSurfaceVariant; padding?: CardSurfacePadding; radius?: CardSurfaceRadius; interactive?: boolean } = {}): string {
  return [styles.card, styles.surface, styles[variant], styles[`pad-${padding}`], styles[`radius-${radius}`], interactive ? styles.interactive : undefined].filter(Boolean).join(" ");
}

export interface CardSurfaceProps extends HTMLAttributes<HTMLElement> {
  /** Rend un `<a href>` (navigation) ; sinon `<div>` ou l'enfant (`asChild`). */
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>["rel"];
  asChild?: boolean;
  /** Surface interactive : levée UIArc au survol (pointeur fin, hors mouvement réduit) + anneau de focus. */
  lift?: boolean;
  variant?: CardSurfaceVariant;
  padding?: CardSurfacePadding;
  radius?: CardSurfaceRadius;
}

/** Surface polymorphe peinte et animée par le canon UIArc (une seule peinture, une seule levée). */
export const CardSurface = forwardRef<HTMLElement, CardSurfaceProps>(function CardSurface({ href, target, rel, asChild = false, lift = false, variant = "raised", padding = "md", radius = "lg", className, ...props }, ref) {
  const { motionProps } = useCardLift(lift);
  const shared = {
    ...(props as HTMLMotionProps<"div">),
    ...(lift ? motionProps : {}),
    className: [cardSurfaceClassName({ variant, padding, radius, interactive: lift }), className].filter(Boolean).join(" "),
    "data-variant": variant,
    "data-padding": padding,
    "data-radius": radius,
    "data-interactive": lift || undefined,
    transition: cardLiftTransition,
  };
  if (href !== undefined) return <motion.a ref={ref as Ref<HTMLAnchorElement>} href={href} target={target} rel={rel} {...(shared as HTMLMotionProps<"a">)} />;
  if (asChild) return <MotionSlot ref={ref} {...(shared as HTMLMotionProps<"div">)} />;
  return <motion.div ref={ref as Ref<HTMLDivElement>} {...shared} />;
});
